import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Button, Stack, TextInput } from "@mantine/core";
import { Cubby } from "../stores/Cubby";
import AdvancedSettings from "./advancedSendLightningSettings";
import { IconSend2 } from "@tabler/icons-react";
import { DEFAULT_PATH_FIND_TIMEOUT_IN_SECONDS } from "../helpers/constants";
import SatoshisInfo from "./interactiveData/satoshisInfo";
import { isTwoFaSatisfied } from "../helpers/sendInvoiceHelpers";

const Keysend = observer(({ cubby }: { cubby: Cubby }) => {
  const [error, setError] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentAmountError, setPaymentAmountError] = useState<string | null>(
    null
  );
  const [btcAmount, setBtcAmount] = useState<string>("0");
  const [outgoingChannelScid, setOutgoingChannelScid] = useState<string | null>(
    null
  );
  const [maxFee, setMaxFee] = useState<string | null>(null);
  const [pathFindingTimeout, setPathFindingTimeout] = useState<string | null>(
    DEFAULT_PATH_FIND_TIMEOUT_IN_SECONDS.toString()
  );
  const [maxFeeError, setMaxFeeError] = useState<string | null>(null);
  const [pathFindingTimeoutError, setPathFindingTimeoutError] = useState<
    string | null
  >(null);
  const [twoFACode, setTwoFACode] = useState<string>("");
  const [twoFAError, setTwoFAError] = useState<string | null>(null); // Error state for 2FA
  const [keysendMessage, setKeysendMessage] = useState<string>(""); // New state for keysend message
  const [keysendMessageError, setKeysendMessageError] = useState<string | null>(
    null
  ); // Error state for keysend message
  const [advancedSettingsOpened, setAdvancedSettingsOpened] = useState(false);
  const [isPaymentInProgress, setIsPaymentInProgress] =
    useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  useEffect(() => {
    cubby.cubbyFastInfo("does_need_2FA_form");
  }, []);

  useEffect(() => {
    // Disable the button if paymentAmount is blank, there are any errors,
    // the keysendMessage exceeds 200 characters, or 2FA is not satisfied
    setIsDisabled(
      !paymentAmount ||
        !!maxFeeError ||
        !!pathFindingTimeoutError ||
        !!paymentAmountError ||
        !!keysendMessageError ||
        !isTwoFaSatisfied(
          cubby.shouldShow2FAFormForPayments,
          twoFACode,
          twoFAError
        )
    );
  }, [
    paymentAmount,
    maxFeeError,
    pathFindingTimeoutError,
    paymentAmountError,
    keysendMessageError,
    cubby.shouldShow2FAFormForPayments,
    twoFACode,
    twoFAError,
  ]);

  const handlePaymentAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    const amount = Math.floor(parseInt(value, 10));
    const maxAmount = cubby.highestOutboundChannelBalance;

    if (isNaN(amount) || amount < 1) {
      setPaymentAmountError("Payment amount must be at least 1 satoshi.");
    } else if (amount > maxAmount) {
      setPaymentAmountError(
        `Payment amount must not exceed ${maxAmount} satoshis.`
      );
    } else {
      setPaymentAmountError(null);
    }

    setPaymentAmount(amount.toString());
    const btcValue = (amount / 100000000).toFixed(8);
    setBtcAmount(isNaN(Number(btcValue)) || btcValue === "0" ? "" : btcValue);
  };

  const handleOutgoingChannelChange = (value: string | null) => {
    setOutgoingChannelScid(value);
  };

  const handleMaxFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const maxFeeValue = Math.floor(parseInt(value, 10));

    if (isNaN(maxFeeValue) || maxFeeValue < 0) {
      setMaxFeeError("Max fee must not be less than 0.");
    } else if (maxFeeValue > Number(paymentAmount) / 2) {
      setMaxFeeError(
        "Max fee must not exceed half the value of the amount to pay."
      );
    } else {
      setMaxFeeError(null);
    }

    setMaxFee(value);
  };

  const handlePathFindingTimeoutChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    const timeoutValue = Math.floor(parseInt(value, 10));

    if (isNaN(timeoutValue) || timeoutValue < 1) {
      setPathFindingTimeoutError(
        "Pathfinding timeout must not be less than 1 second."
      );
    } else if (timeoutValue > 240) {
      setPathFindingTimeoutError(
        "Pathfinding timeout must not exceed 240 seconds."
      );
    } else {
      setPathFindingTimeoutError(null);
    }

    setPathFindingTimeout(value);
  };

  const handleKeysendMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    if (value.length > 200) {
      setKeysendMessageError("Message must be 200 characters or less.");
    } else {
      setKeysendMessageError(null);
    }
    setKeysendMessage(value);
  };

  const handleTwoFACodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value.replace(/\D/g, ""); // Only allow digits
    setTwoFACode(value);

    // Validate if the code has exactly six digits
    if (value.length > 0 && value.length !== 6) {
      setTwoFAError("A 2FA Code must be six digits.");
    } else {
      setTwoFAError(null);
    }
  };

  const handleKeysendPayment = () => {
    const amountToPay = paymentAmount;
    const maxFeeValue = maxFee ? parseInt(maxFee, 10) : null;
    const pathFindingTimeoutValue = pathFindingTimeout
      ? parseInt(pathFindingTimeout, 10)
      : DEFAULT_PATH_FIND_TIMEOUT_IN_SECONDS;

    const dataHash = {
      amountInSatoshis: amountToPay,
      outgoingChannelScid,
      maxFee: maxFeeValue,
      pathFindingTimeout: pathFindingTimeoutValue,
      destinationPublicKey: cubby.lastProposedPublicKeyForKeysend,
      twoFAcode: twoFACode,
      keysendMessage: keysendMessage || null, // Include keysendMessage, default to null if not entered
    };

    console.log("paying with", dataHash);
    cubby.cubbyLife("dangerous_pay_keysend", dataHash);

    setIsPaymentInProgress(true);
    setTimeout(() => {
      setIsPaymentInProgress(false);
    }, 2000);
  };

  return (
    <Stack>
      <TextInput
        placeholder="Enter destination public key"
        value={cubby.lastProposedPublicKeyForKeysend}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          cubby.handleKeysendPublicKeyChange(e.target.value)
        }
        error={cubby.lastKeysendErrormessage}
      />
      {cubby.lastProposedPublicKeyForKeysend.length > 0 &&
        cubby.lastKeysendErrormessage === "" && (
          <>
            <TextInput
              type="number"
              label="Payment Amount (satoshis)"
              placeholder="Enter payment amount"
              value={paymentAmount}
              onChange={handlePaymentAmountChange}
              error={paymentAmountError}
            />
            <SatoshisInfo cubby={cubby} satoshis={parseInt(paymentAmount)} />

            <TextInput
              label="Message (optional)"
              placeholder="Type an optional message"
              value={keysendMessage}
              onChange={handleKeysendMessageChange}
              error={keysendMessageError}
            />

            {cubby.shouldShow2FAFormForPayments && (
              <TextInput
                label="Enter 2FA Code"
                maxLength={6}
                placeholder="Get the code from your authenticator app"
                value={twoFACode}
                onChange={handleTwoFACodeChange}
                error={twoFAError}
                mt="md"
              />
            )}

            <AdvancedSettings
              advancedSettingsOpened={advancedSettingsOpened}
              setAdvancedSettingsOpened={setAdvancedSettingsOpened}
              possibleChannels={cubby.outBoundChannelChoices(
                Number(paymentAmount)
              )}
              outgoingChannelScid={outgoingChannelScid}
              handleOutgoingChannelChange={handleOutgoingChannelChange}
              maxFee={maxFee}
              handleMaxFeeChange={handleMaxFeeChange}
              maxFeeError={maxFeeError}
              pathFindingTimeout={pathFindingTimeout}
              handlePathFindingTimeoutChange={handlePathFindingTimeoutChange}
              pathFindingTimeoutError={pathFindingTimeoutError}
            />

            <Button
              onClick={handleKeysendPayment}
              rightSection={<IconSend2 />}
              loading={isPaymentInProgress}
              disabled={isDisabled}
            >
              Pay By Keysend
            </Button>
          </>
        )}
    </Stack>
  );
});

export default Keysend;
